
<template>
  <div class="workspace-wrapper">
    <div class="switcher-container shadow-sm border rounded bg-white d-flex align-items-center px-3 py-2">
      <div class="context-icon mr-3 d-flex align-items-center justify-content-center rounded-circle" 
           :class="modelValue ? 'bg-light-blue' : 'bg-light-yellow'">
        <i :class="modelValue ? 'fas fa-users text-primary' : 'fas fa-user-lock text-warning'"></i>
      </div>

      <div class="flex-grow-1">
        <label class="text-muted d-block mb-0 font-weight-bold text-uppercase" style="font-size: 0.65rem; letter-spacing: 1px;">
          Workspace
        </label>
        <select 
          :value="modelValue" 
          @change="$emit('update:modelValue', $event.target.value === 'null' ? null : Number($event.target.value))"
          class="custom-select border-0 p-0 shadow-none font-weight-bold text-dark w-100"
          style="height: auto; background: none; cursor: pointer;"
          :disabled="isLoading"
        >
          <option :value="null">My Personal Space</option>
          <optgroup v-if="groups.length > 0" label="Teams">
            <option v-for="group in groups" :key="group.id" :value="group.id">
              {{ group.name }}
            </option>
          </optgroup>
        </select>
      </div>

      <button 
        v-if="modelValue" 
        @click="openSettings" 
        class="btn btn-link p-0 ml-2 text-primary shadow-none"
        title="Manage Members"
      >
        <i class="fas fa-cog"></i>
      </button>

      <div v-if="isLoading" class="spinner-border spinner-border-sm text-primary ml-2" role="status"></div>
    </div>

     <teleport to="body">
  <div class="modal fade" id="groupSettingsModal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content border-0 shadow-lg rounded-lg overflow-hidden">
        
        <div class="modal-header border-0 bg-dark text-white p-4">
          <h5 class="modal-title font-weight-bold">
            <i class="fas fa-users-cog mr-2"></i> Team Settings
          </h5>
          <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close" @click="closeSettings">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body p-4 bg-light">
          
          <div v-if="currentUserRole === 'admin'" class="mb-4">
            <label class="text-muted small font-weight-bold text-uppercase mb-2 d-block">Invite Member</label>
            <div class="input-group shadow-sm rounded-pill overflow-hidden bg-white border">
              <input type="email" v-model="newMemberEmail" class="form-control border-0 ps-3 small" placeholder="User email...">
              <div class="input-group-append">
                <button class="btn btn-primary px-3" @click="addMember" :disabled="isInviting">
                  <i v-if="isInviting" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-user-plus"></i>
                </button>
              </div>
            </div>
          </div>

          <label class="text-muted small font-weight-bold text-uppercase mb-2 d-block">Members</label>
          <div class="members-list rounded shadow-sm bg-white overflow-hidden border">
            
            <div v-if="isLoadingMembers" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-primary"></div>
            </div>

            <template v-else>
              <div v-for="member in members" :key="member.id" 
                   class="d-flex align-items-center justify-content-between p-3 border-bottom last-border-0">
                
                <div class="d-flex align-items-center">
                  <div class="avatar-circle-sm bg-primary text-white mr-3 d-flex align-items-center justify-content-center overflow-hidden" 
                       style="width:35px; height:35px; border-radius:50%">
                    <img v-if="member.profile" :src="member.profile" class="w-100 h-100 object-fit-cover">
                    <span v-else>{{ member.username?.charAt(0).toUpperCase() }}</span>
                  </div>
                  
                  <div>
                    <h6 class="mb-0 small font-weight-bold">{{ member.username }}</h6>
                    <span class="badge badge-pill extra-small" 
                          :class="member.role === 'admin' ? 'badge-danger' : 'badge-primary'">
                      {{ member.role }}
                    </span>
                  </div>
                </div>

                <button v-if="currentUserRole === 'admin' && member.id !== currentUserId" 
                        @click="removeMember(member.id)"
                        class="btn btn-link text-danger p-0">
                  <i class="far fa-trash-alt"></i>
                </button>
              </div>
            </template>
          </div>

        </div>
      </div>
    </div>
  </div>
</teleport>
  </div>
</template>

<script>
 
import { groupsService } from "@/services/groupsService";
import Swal from "sweetalert2";

export default {
  name: "WorkspaceSwitcher",
  props: {
    modelValue: { type: [Number, String], default: null }
  },
  data() {
    return {
      groups: [],
      members: [],
      newMemberEmail: "",
      isLoading: false,
      isLoadingMembers: false,
      isInviting: false,
      currentUserRole: 'member'
    };
  },
  computed: {
    currentUserId() {
       
      return this.$store.state.user?.id;
    }
  },
  async mounted() {
    await this.fetchGroups();
  },

 watch: {
    
    modelValue: {
      immediate: true,  
      async handler(newVal) {
        if (newVal) {
           
          await this.fetchMembersOnly();
          
           
          const me = this.members.find(m => m.id === this.currentUserId);
          this.currentUserRole = me ? me.role : 'member';
          
          this.$emit('role-updated', this.currentUserRole);
        } else {
           
          this.members = [];
          this.$emit('members-updated', []);
        }
      }
    }
  },

  methods: {
    async fetchGroups() {
      this.isLoading = true;
      try {
        this.groups = await groupsService.getMyGroups();
      } catch (err) {
        this.error = "Sync failed";
      } finally {
        this.isLoading = false;
      }
    },

    async openSettings() {
  this.isLoadingMembers = true;
  try {
  
    this.members = await groupsService.getGroupMembers(this.modelValue);
    
    
    const me = this.members.find(m => m.id === this.currentUserId);
    this.currentUserRole = me ? me.role : 'member';

     

   
    this.$nextTick(() => {
      const modal = window.$('#groupSettingsModal');
      if (modal.length) {
        modal.modal('show');
      } else {
        console.error("Modal #groupSettingsModal not found in DOM");
      }
    });

  } catch (err) {
    Swal.fire("Error", "Could not load members", "error");
     
  } finally {
    this.isLoadingMembers = false;
  }
},

  async addMember() {
    if (!this.newMemberEmail.trim()) {
        return Swal.fire("Attention", "Please enter a valid email", "warning");
    }

    this.isInviting = true;
    try {
        await groupsService.addMemberByEmail(this.modelValue, this.newMemberEmail);
        
       
        this.newMemberEmail = "";
        
        
        await this.fetchMembersOnly(); 
        
        Swal.fire({
            title: "Added!",
            text: "Member has been added successfully",
            icon: "success",
            timer: 1500,
            showConfirmButton: false
        });
    } catch (err) {
        const errorMsg = err.response?.data || "User not found";
        Swal.fire("Error", errorMsg, "error");
    } finally {
        this.isInviting = false;
    }
},
async removeMember(memberId) {
   
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: "This member will be removed from the team",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, remove them!'
  });

  if (result.isConfirmed) {
    try {
      
      await groupsService.removeMember(this.modelValue, memberId);
      
      
      this.members = this.members.filter(m => m.id !== memberId);

      Swal.fire(
        'Removed!',
        'The member has been removed.',
        'success'
      );
    } catch (err) {
      const errorMsg = err.response?.data || "Failed to remove member";
      Swal.fire("Error", errorMsg, "error");
    }
  }
},
 
async fetchMembersOnly() {
    try {
        this.members = await groupsService.getGroupMembers(this.modelValue);
        this.$emit('members-updated', this.members);
    } catch (err) {
        throw err;
    }
}
  }
};
 
 
 
 
</script>

<style scoped>
.workspace-wrapper {
  min-width: 240px;
}

.switcher-container {
  transition: all 0.2s;
  border-color: #dee2e6 !important;
}

.switcher-container:hover {
  border-color: #007bff !important;
  background-color: #f8f9fa;
}

 
.bg-light-blue { background-color: #e7f1ff; }
.bg-light-yellow { background-color: #fff9e6; }

.context-icon {
  width: 35px;
  height: 35px;
  flex-shrink: 0;
}

 
.custom-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}



.extra-small { font-size: 0.65rem; }
.last-border-0:last-child { border-bottom: 0 !important; }
.bg-danger-subtle { background-color: #f8d7da; }
.bg-primary-subtle { background-color: #cfe2ff; }
.object-fit-cover { object-fit: cover; }

</style>